'use client';

import { useRouter } from 'next/navigation';
import { ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { BriefingView } from './briefing-view';
import {
  DEMO_COMPANY_NAME,
  getDemoBriefingMarkdown,
} from '@/lib/demo-briefing';

// ---------------------------------------------------------------------------
// DemoBriefingModal — full-screen modal showing a mock briefing (PROJ-29)
// ---------------------------------------------------------------------------

interface DemoBriefingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Override the demo content. Defaults to the insurance demo (Muster Drive Systems GmbH). */
  companyName?: string;
  markdown?: string;
  /** Where to send the user when they click the primary CTA. */
  ctaHref?: string;
  /** Label for the modal badge. */
  badgeLabel?: string;
}

export function DemoBriefingModal({
  open,
  onOpenChange,
  companyName,
  markdown,
  ctaHref = '/register?ref=demo-cta',
  badgeLabel = 'Demo-Beispiel',
}: DemoBriefingModalProps) {
  const router = useRouter();

  const resolvedCompanyName = companyName ?? DEMO_COMPANY_NAME;
  const resolvedMarkdown = markdown ?? getDemoBriefingMarkdown();

  const handleCtaClick = () => {
    onOpenChange(false);
    router.push(ctaHref);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={true}
        className="!max-w-[96vw] !w-[96vw] !h-[95vh] !max-h-[95vh] flex flex-col p-0 gap-0 overflow-hidden"
      >
        {/* Modal header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border flex-shrink-0 pr-12">
          <div className="flex items-center gap-3 min-w-0">
            <DialogTitle className="text-base font-semibold truncate">
              {resolvedCompanyName}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Demo-Briefing für {resolvedCompanyName}
            </DialogDescription>
            <Badge
              variant="secondary"
              className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 flex-shrink-0"
            >
              {badgeLabel}
            </Badge>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={handleCtaClick}
            className="flex-shrink-0 hidden sm:flex"
          >
            Jetzt kostenlos testen
            <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Scrollable briefing body */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <BriefingView
            briefingId="demo"
            companyName={resolvedCompanyName}
            createdAt={new Date().toISOString()}
            resultMarkdown={resolvedMarkdown}
            demoMode
          />
        </div>

        {/* Mobile CTA — fixed at bottom on small screens */}
        <div className="sm:hidden border-t border-border p-3 flex-shrink-0">
          <Button
            variant="default"
            size="sm"
            onClick={handleCtaClick}
            className="w-full"
          >
            Jetzt kostenlos testen
            <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
